import React, { createContext, useContext, useMemo, useEffect } from "react";
import { useFeatureValue } from "@growthbook/growthbook-react";
import { growthBookInstances } from "../constants";
import { setLanderContent } from "../utils/analytics";
import {
  DEFAULT_PRICE_MULTIPLIER,
  LOCATION_PRICE_MULTIPLIER_FLAG,
  resolvePriceMultiplier,
} from "../utils/paywall";

const GrowthBookContext = createContext(null);

// Nav-config types that need sticky (per-tab) resolution because their GB rules
// are URL-targeted and the quiz/home SPA navigation strips the query params.
const STICKY_NAV_TYPES = ["paywall_popup", "simple_registration"];

export const GrowthBookProvider = ({ children }) => {
  const content = useFeatureValue(
    "createsexfriend-content",
    growthBookInstances,
  );
  const paywallTheme = useFeatureValue(
    "createsexfriends-paywall-theme",
    "default",
  );
  const animatedPictures = useFeatureValue(
    "createsexfriend-animated-pictures",
    "AUTOMATIC",
  );
  const steps = useFeatureValue("createsexfriend-steps", "BASE");
  const attributeSelectionStyle = useFeatureValue(
    "createsexfriend-attribute-selection-style",
    "DEFAULT",
  );
  const magicLink = useFeatureValue("createsexfriends-white-magicLink", "OFF");

  // GB-controlled header video for the paywall popup (platform-global flag name,
  // shared with the sibling create-friend lander).
  const paywallPopupVideo = useFeatureValue("paywall_popup_video", "");

  // Platform-global token-bonus experiments driving the paywall popup's heart pill
  // (same flag names/defaults as useTokenBonus + the platform). Exposed here so the
  // auth flow can forward them in `ab_testing` for per-variant conversion attribution.
  const premiumTokensAmountRaw = useFeatureValue("premium-tokens-amount", "");
  const premiumInstantTokensRaw = useFeatureValue(
    "premium-instant-tokens-amount",
    "0|0|0",
  );

  // Platform-global rebill experiment ('control' | 'flat_rebill'), read here for the
  // same reason as the token flags above: the popup's `/subscription-plans` call is
  // cross-origin and x-api-key-only, so the backend has no user to resolve
  // `plan.isFlatRebill` against (it comes from
  // user.marketing.experiments['exp-full-rebill'], and ParseJwtInterceptor only reads
  // the access_token COOKIE) and always returns false. Without this the Upgate hosted
  // page quotes the undiscounted renewal price to flat_rebill users.
  const fullRebillExpRaw = useFeatureValue("exp_full_rebill", "");

  // Platform-global geo price multiplier, read here for the same reason: the popup's
  // plans come back UNMULTIPLIED (the backend only applies it for an authenticated
  // request), while the Upgate charge it builds is multiplied — so any amount the
  // lander prints itself has to be scaled by this or it understates the bill.
  // Country-targeted, so it only resolves once App.jsx's geo lookup lands and the
  // attributes are re-applied (this re-renders when that happens).
  const priceMultiplierRaw = useFeatureValue(
    LOCATION_PRICE_MULTIPLIER_FLAG,
    DEFAULT_PRICE_MULTIPLIER,
  );
  // Sticky resolution for the token-bonus EXPERIMENTS (same reasoning as the
  // nav-config stickiness below). These flags resolve to a numeric variant. On the
  // Google OAuth return the popup re-opens on a fresh, param-less load where GB
  // hasn't re-resolved yet, so a live read falls back to the settings/hardcoded
  // number instead of the variant that was actually sent at registration (load A)
  // and stored in marketing.experiments. We remember the first resolved (non-default)
  // value for this tab and reuse it, so BOTH the ab_testing send and the returning
  // popup show exactly the same variant. sessionStorage survives the same-tab OAuth
  // round-trip (like the nav-config key).
  // Storage keys mirror the platform-GLOBAL GB flag names (not lander-scoped) —
  // these experiments are the same across every lander.
  const PREMIUM_TOKENS_KEY = "premium-tokens-amount";
  const PREMIUM_INSTANT_KEY = "premium-instant-tokens-amount";
  const FULL_REBILL_KEY = "exp_full_rebill";
  // FIRST-write-wins per tab: the first resolved (non-default) value is frozen and
  // reused for the rest of the visit. That first value is what gets sent in
  // ab_testing and stored in marketing.experiments on load A, so the popup on the
  // OAuth return MUST keep showing it even if GB re-resolves to a DIFFERENT variant
  // on the param-less return (e.g. a utm/url-targeted rule that no longer matches
  // once the campaign params are gone). We therefore prefer the stored value over a
  // later live re-resolution — never overwrite it.
  const premiumTokensAmount = useMemo(() => {
    if (typeof window === "undefined") return premiumTokensAmountRaw;
    try {
      const stored = sessionStorage.getItem(PREMIUM_TOKENS_KEY);
      if (stored) return stored;
    } catch (e) {
      /* ignore */
    }
    if (premiumTokensAmountRaw !== "" && premiumTokensAmountRaw != null) {
      try {
        sessionStorage.setItem(
          PREMIUM_TOKENS_KEY,
          String(premiumTokensAmountRaw),
        );
      } catch (e) {
        /* ignore */
      }
      return premiumTokensAmountRaw;
    }
    return premiumTokensAmountRaw;
  }, [premiumTokensAmountRaw]);
  const premiumInstantTokens = useMemo(() => {
    if (typeof window === "undefined") return premiumInstantTokensRaw;
    try {
      const stored = sessionStorage.getItem(PREMIUM_INSTANT_KEY);
      if (stored) return stored;
    } catch (e) {
      /* ignore */
    }
    if (premiumInstantTokensRaw && premiumInstantTokensRaw !== "0|0|0") {
      try {
        sessionStorage.setItem(
          PREMIUM_INSTANT_KEY,
          String(premiumInstantTokensRaw),
        );
      } catch (e) {
        /* ignore */
      }
      return premiumInstantTokensRaw;
    }
    return premiumInstantTokensRaw;
  }, [premiumInstantTokensRaw]);
  // Same first-write-wins stickiness: in popup_first the plan is picked on load A but
  // charged on load B (the Google OAuth return), and the checkout copy is built at
  // charge time — the visitor must be quoted the renewal price they picked under.
  const fullRebillExp = useMemo(() => {
    if (typeof window === "undefined") return fullRebillExpRaw;
    try {
      const stored = sessionStorage.getItem(FULL_REBILL_KEY);
      if (stored) return stored;
    } catch (e) {
      /* ignore */
    }
    // Only freeze a real resolution — an empty value means the flag is missing or GB
    // hasn't answered yet, and must not be cached as if it were a variant.
    if (fullRebillExpRaw) {
      try {
        sessionStorage.setItem(FULL_REBILL_KEY, String(fullRebillExpRaw));
      } catch (e) {
        /* ignore */
      }
    }
    return fullRebillExpRaw;
  }, [fullRebillExpRaw]);
  // Same stickiness for the multiplier, with one extra rule: only a NON-DEFAULT value
  // is frozen. It resolves to 1 until the country lookup lands, and freezing that
  // would keep the tab on base prices for the rest of the visit even after the geo
  // arrives. 1 is also the safe fallback (no scaling).
  const priceMultiplier = useMemo(() => {
    const live = resolvePriceMultiplier(priceMultiplierRaw);
    if (typeof window === "undefined") return live;
    try {
      const stored = sessionStorage.getItem(LOCATION_PRICE_MULTIPLIER_FLAG);
      if (stored) return resolvePriceMultiplier(stored);
    } catch (e) {
      /* ignore */
    }
    if (live !== DEFAULT_PRICE_MULTIPLIER) {
      try {
        sessionStorage.setItem(LOCATION_PRICE_MULTIPLIER_FLAG, String(live));
      } catch (e) {
        /* ignore */
      }
    }
    return live;
  }, [priceMultiplierRaw]);

  // Post-auth navigation config (JSON flag: { type, flow }).
  //   type 'paywall'       → existing behaviour (redirect to /paywall);
  //   type 'chat_paywall'  → redirect to /lobby, model-params carried in query;
  //   type 'paywall_popup' → open the in-place paywall popup after signup
  //                          (no redirect); the popup → Upgate → /lobby flow.
  //   type 'simple_registration' → AB test (86ca50q0c): no email on the LP. On the
  //                          summary step (Step5) a dummy user is created, then the
  //                          user is sent to /paywall with auth_flow=tempLogin; the
  //                          real email is collected on the platform checkout.
  //   flow 'chat'|'generation' → only meaningful for paywall_popup; tells /lobby
  //                          where to route after payment. Defaults to 'generation'.
  //   order 'after_signup'|'popup_first' → only meaningful for paywall_popup; which
  //                          side of the signup screen the popup sits on:
  //                            'after_signup' (default) signup → popup → Upgate;
  //                            'popup_first'            popup → signup → Upgate
  //                          (the popup is shown to an UNAUTHENTICATED user, who
  //                          picks a plan; checkout fires right after registration).
  const paywallNavConfig = useFeatureValue(
    "createsexfriends-white-paywall-nav-config",
    { type: "paywall" },
  );

  // Sticky (per-tab) resolution — see STICKY_NAV_TYPES. Their GB rules are
  // URL-targeted (e.g. ?paywallpopup=true, ?cmpid=simpleReg), but the quiz/home
  // SPA navigation strips query params, so GB re-resolves on a param-less URL and
  // flips back to the default before the user reaches signup / the summary. To
  // keep the flow stable, we remember the config the first time GB resolves a
  // sticky type, and fall back to it on later renders when the live value reverted
  // to the default. sessionStorage scopes it to this tab/visit — it survives the
  // funnel's SPA navigation (and the Google OAuth round-trip in the same tab) but
  // does NOT pin future sessions. Only STICKY_NAV_TYPES are made sticky; the other
  // types keep live behaviour.
  const PERSIST_KEY = "csfw_paywall_nav_config";
  const stickyConfig = useMemo(() => {
    if (typeof window === "undefined") return null;
    if (STICKY_NAV_TYPES.includes(paywallNavConfig?.type)) {
      try {
        sessionStorage.setItem(PERSIST_KEY, JSON.stringify(paywallNavConfig));
      } catch (e) {
        // ignore storage failures
      }
      return paywallNavConfig;
    }
    try {
      const raw = sessionStorage.getItem(PERSIST_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (STICKY_NAV_TYPES.includes(parsed?.type)) return parsed;
    } catch (e) {
      // ignore
    }
    return null;
  }, [paywallNavConfig]);

  const resolvedNavConfig = stickyConfig || paywallNavConfig;
  const paywallNavType = resolvedNavConfig?.type || "paywall";
  const paywallFlow = resolvedNavConfig?.flow || "generation";
  // 'after_signup' keeps the original paywall_popup behaviour, so an existing flag
  // value without an `order` field is unaffected.
  const paywallOrder = resolvedNavConfig?.order || "after_signup";

  // Forward the content config's `name` to analytics as `lander-content`, so it
  // rides along on every GTM event regardless of which variation is shown.
  useEffect(() => {
    if (content?.name) setLanderContent(content.name);
  }, [content]);

  // Persist the resolved nav type so the redirect builders in utils/api.js + utils/paywall.js
  // (and the post-OAuth return handler, which runs before GrowthBook re-initializes) can read
  // it from localStorage without threading it through every call site.
  useEffect(() => {
    if (paywallNavType) {
      localStorage.setItem("paywallNavType", paywallNavType);
    }
  }, [paywallNavType]);

  const values = useMemo(
    () => ({
      content: content || growthBookInstances,
      paywallTheme: paywallTheme || "default",
      animatedPictures: animatedPictures || "AUTOMATIC",
      steps: steps || "BASE",
      attributeSelectionStyle: attributeSelectionStyle || "DEFAULT",
      magicLink: magicLink || "OFF",
      paywallNavType: paywallNavType || "paywall",
      paywallFlow: paywallFlow || "generation",
      paywallOrder: paywallOrder || "after_signup",
      paywallPopupVideo: paywallPopupVideo || "",
      premiumTokensAmount: premiumTokensAmount ?? "",
      premiumInstantTokens: premiumInstantTokens ?? "0|0|0",
      // Renewals charge the same amount as the first payment instead of the
      // undiscounted price — drives the Upgate fine print, see useUpgateCheckout.js.
      isFlatRebill: fullRebillExp === "flat_rebill",
      // Geo price multiplier the backend bills with — scales the amounts the Upgate
      // fine print quotes, see useUpgateCheckout.js.
      priceMultiplier,
    }),
    [
      content,
      paywallTheme,
      animatedPictures,
      steps,
      attributeSelectionStyle,
      magicLink,
      paywallNavType,
      paywallFlow,
      paywallOrder,
      paywallPopupVideo,
      premiumTokensAmount,
      premiumInstantTokens,
      fullRebillExp,
      priceMultiplier,
    ],
  );

  return (
    <GrowthBookContext.Provider value={values}>
      {children}
    </GrowthBookContext.Provider>
  );
};

export const useGrowthBookValues = () => {
  const context = useContext(GrowthBookContext);
  if (!context) {
    throw new Error(
      "useGrowthBookValues must be used within GrowthBookProvider",
    );
  }
  return context;
};
